
import * as React from "react";
import { format, parseISO, isWithinInterval, startOfDay, endOfDay, eachDayOfInterval } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { SiteBooking } from "@/hooks/useSiteBookings";

interface BookingCalendarProps {
  date: Date | undefined;
  setDate: (date: Date | undefined) => void;
  label?: string;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  bookings?: SiteBooking[];
  disablePastDates?: boolean;
  mode?: "checkin" | "checkout";
  defaultMonth?: Date;
  highlightDate?: Date;
  rangeEnd?: Date;
}

export function BookingCalendar({
  date,
  setDate,
  label,
  className,
  placeholder = "Select date",
  disabled = false,
  bookings = [],
  disablePastDates = true,
  mode = "checkin",
  defaultMonth,
  highlightDate,
  rangeEnd,
}: BookingCalendarProps) {
  const today = React.useMemo(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }, []);

  // Create list of all booked dates based on mode
  // Check-in mode: Block nights that are occupied (start through day before checkout)
  // Check-out mode: Allow same-day turnover by blocking only dates where guest is still staying
  const bookedDates = React.useMemo(() => {
    const dates: Date[] = [];
    
    bookings.forEach(booking => {
      try {
        const startDate = startOfDay(parseISO(booking.start_date));
        const endDate = startOfDay(parseISO(booking.end_date));
        
        if (mode === "checkout") {
          // For checkout: allow selecting the start date of another booking (same-day turnover)
          // Block from (startDate + 1) through endDate
          if (startDate < endDate) {
            const firstBlockedDay = new Date(startDate);
            firstBlockedDay.setDate(firstBlockedDay.getDate() + 1);
            
            // Only add dates if there's a valid range
            if (firstBlockedDay <= endDate) {
              const datesInRange = eachDayOfInterval({ start: firstBlockedDay, end: endDate });
              dates.push(...datesInRange);
            }
          }
        } else {
          // Check-in mode (default): Block nights that are occupied
          // Exclude end date (checkout day) - it should be available for new check-ins
          if (startDate < endDate) {
            const lastNight = new Date(endDate);
            lastNight.setDate(lastNight.getDate() - 1);
            
            const datesInRange = eachDayOfInterval({ start: startDate, end: lastNight });
            dates.push(...datesInRange);
          }
        }
      } catch (error) {
        console.error('Error processing booking date range:', error, booking);
      }
    });
    
    return dates;
  }, [bookings, mode]);

  // Debug logging to help troubleshoot
  React.useEffect(() => {
    if (bookings.length > 0) {
      console.log('BookingCalendar - Bookings received:', bookings);
      console.log('BookingCalendar - Calculated booked dates:', bookedDates.map(d => format(d, 'yyyy-MM-dd')));
    }
  }, [bookings, bookedDates]);

  // Compute stay range dates (between check-in and check-out)
  const stayRangeDates = React.useMemo(() => {
    if (!highlightDate || !rangeEnd || highlightDate >= rangeEnd) return [];
    const dayAfterCheckin = new Date(highlightDate);
    dayAfterCheckin.setDate(dayAfterCheckin.getDate() + 1);
    const dayBeforeCheckout = new Date(rangeEnd);
    dayBeforeCheckout.setDate(dayBeforeCheckout.getDate() - 1);
    if (dayAfterCheckin > dayBeforeCheckout) return [];
    return eachDayOfInterval({ start: dayAfterCheckin, end: dayBeforeCheckout });
  }, [highlightDate, rangeEnd]);

  const modifiers = React.useMemo(() => ({
    booked: bookedDates,
    ...(highlightDate ? { checkinDay: highlightDate } : {}),
    ...(stayRangeDates.length > 0 ? { stayRange: stayRangeDates } : {}),
  }), [bookedDates, highlightDate, stayRangeDates]);

  const modifiersStyles = React.useMemo(() => ({
    booked: {
      backgroundColor: '#fed7aa',
      color: '#c2410c',
      fontWeight: 'bold',
    },
    ...(highlightDate ? {
      checkinDay: {
        backgroundColor: '#bfdbfe',
        color: '#1d4ed8',
        fontWeight: 'bold' as const,
      },
    } : {}),
    ...(stayRangeDates.length > 0 ? {
      stayRange: {
        backgroundColor: '#dbeafe',
        color: '#1e40af',
      },
    } : {}),
  }), [highlightDate, stayRangeDates]);

  // Combine all disabled conditions into a single disabled array
  const disabledDates = React.useMemo(() => {
    const disabled = [];
    
    // Add past dates if disablePastDates is true
    if (disablePastDates) {
      disabled.push({ before: today });
    }
    
    // Add all booked dates as disabled
    bookedDates.forEach(bookedDate => {
      disabled.push(bookedDate);
    });
    
    return disabled;
  }, [disablePastDates, today, bookedDates]);

  return (
    <div className={className}>
      {label && (
        <label className="text-sm font-medium mb-2 block text-tribal-secondary">
          {label}
        </label>
      )}
      <Popover modal={true}>
        <PopoverTrigger asChild>
          <Button
            variant={"outline"}
            className={cn(
              "w-full justify-start text-left font-normal",
              !date && "text-muted-foreground"
            )}
            disabled={disabled}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {date ? format(date, "PPP") : <span>{placeholder}</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={date}
            onSelect={setDate}
            defaultMonth={defaultMonth || date || undefined}
            initialFocus
            fixedWeeks
            className={cn("p-3 pointer-events-auto")}
            classNames={{
              day_selected:
                "bg-blue-600 text-white hover:bg-blue-700 hover:text-white focus:bg-blue-700 focus:text-white",
            }}
            disabled={disabledDates}
            modifiers={modifiers}
            modifiersStyles={modifiersStyles}
          />
        {(bookings.length > 0 || highlightDate) && (
            <div className="p-3 border-t border-border space-y-1">
              {highlightDate && (
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <div className="w-3 h-3 rounded border" style={{ backgroundColor: '#bfdbfe', borderColor: '#93c5fd' }}></div>
                  <span>Check-in date</span>
                </div>
              )}
              {stayRangeDates.length > 0 && (
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <div className="w-3 h-3 rounded border" style={{ backgroundColor: '#dbeafe', borderColor: '#bfdbfe' }}></div>
                  <span>Your stay</span>
                </div>
              )}
              {bookings.length > 0 && (
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <div className="w-3 h-3 bg-orange-200 rounded border border-orange-300"></div>
                  <span>Booked dates</span>
                </div>
              )}
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
